'use client';

import { RoundButton } from '@/components/buttons/Buttons';
import RadioButtonInput from '@/components/form-inputs/RadioButtonInput';
import '@/components/program/travel-customization.styles.css';
import { TextBodyExtraExtraSmall } from '@/components/text/Texts';
import { TRAVEL_PROGRAM_FORM } from '@/constants/forms';
import { TRAVEL_TIME_TYPE } from '@/constants/travel-program';
import FooterContent from '@/modules/account/components/travel-customization/FooterContent';
import HeaderContent from '@/modules/account/components/travel-customization/HeaderContent';
import { isEmpty } from 'lodash';
import type { DialogProps } from 'primereact/dialog';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FaClock } from 'react-icons/fa';
import AddCustomTimeModal from './AddCustomTimeModal';
import SecondaryModal from './SecondaryModal';

interface ModalProps extends DialogProps {
	setVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const TimeModal = (props: ModalProps) => {
	const { setVisible, ...rest } = props;
	const [showAddTimeModal, setShowAddTimeModal] = useState(false);

	const {
		control,
		resetField,
		setValue,
		trigger,
		watch,
		formState: { errors },
	} = useFormContext();

	const timeTypeValue = watch('timeType');
	const isAllDayValue = watch('isAllDay');
	const startTimeValue = watch('startTime');
	const endTimeValue = watch('endTime');
	const daysValue = watch('days');
	const hasValidTime =
		!isEmpty(daysValue) &&
		!isEmpty(startTimeValue) &&
		!isEmpty(endTimeValue) &&
		!errors.days &&
		!errors.startTime &&
		!errors.endTime;

	const handleOnDelete = () => {
		setValue('days', []);
		resetField('startTime');
		resetField('endTime');
		resetField('isAllDay');
	};

	const handleOnCancel = useCallback(() => {
		setVisible(false);
	}, [setVisible]);

	const handleOnSave = useCallback(() => {
		trigger(['timeType', 'days', 'startTime', 'endTime']).then(() => {
			if (
				timeTypeValue === TRAVEL_TIME_TYPE.custom &&
				errors.days &&
				errors.startTime &&
				errors.endTime
			)
				return;
			setVisible(false);
		});
	}, [
		setVisible,
		trigger,
		timeTypeValue,
		errors.days,
		errors.startTime,
		errors.endTime,
	]);

	const handleChange = (value: string) => {
		setValue('timeType', value);
	};

	const handleKeyDown = (e: React.KeyboardEvent, value: string) => {
		if (e.key === 'Enter' || e.key === ' ') {
			handleChange(value);
		}
	};

	const addTime = (
		<div>
			<div className="flex justify-between items-center mt-6 mb-4">
				<h1 className="text-base">Custom Time</h1>
				<RoundButton
					className="secondary"
					formName={TRAVEL_PROGRAM_FORM}
					label="Add Custom Time"
					onClick={() => {
						setShowAddTimeModal(true);
					}}
					id="show-time-show"
				/>
			</div>
			{hasValidTime ? (
				<div className="bg-primary100 flex justify-between p-3 rounded-md">
					<div>
						<p className="text-lg text-body-large text-onPrimaryFixedVariant">
							{isAllDayValue
								? 'All Day'
								: `${startTimeValue} - ${endTimeValue}`}
						</p>
						<p className="uppercase text-body-small text-primary mt-2">
							{daysValue.join(', ')}
						</p>
					</div>
					<div className="flex gap-2">
						<RoundButton
							className="secondary"
							formName={TRAVEL_PROGRAM_FORM}
							icon={'pi pi-pencil'}
							id="edit-time"
							onClick={() => setShowAddTimeModal(true)}
						/>
						<RoundButton
							className="secondary"
							formName={TRAVEL_PROGRAM_FORM}
							icon={'pi pi-trash'}
							id="delete-time"
							onClick={handleOnDelete}
						/>
					</div>
				</div>
			) : (
				<>
					<div className="bg-secondaryDim flex justify-center p-7 rounded-md">
						<p className="text-primaryContainer text-xs">
							No time added yet.
						</p>
					</div>
					{errors.days && errors.startTime && errors.endTime && (
						<TextBodyExtraExtraSmall
							id={'days-helper'}
							className="block mt-1 text-error"
							text="Please add a custom time."
						/>
					)}
				</>
			)}
		</div>
	);

	return (
		<>
			<SecondaryModal
				{...rest}
				draggable={false}
				header={
					<HeaderContent
						label="Time"
						icon={<FaClock size={30} className="text-primary" />}
					/>
				}
				footer={
					<FooterContent
						formName={TRAVEL_PROGRAM_FORM}
						id="time"
						onClickCancel={handleOnCancel}
						onClickSave={handleOnSave}
					/>
				}
				style={{ width: '45rem' }}
			>
				<div className="font-poppins">
					<div className="mb-6">
						<p className="text-gray-700">
							When should people be able to ride on this travel
							program?
						</p>
					</div>

					<div>
						<div
							className={
								'flex items-center justify-between py-3 border-b border-slate-300 cursor-pointer'
							}
							onClick={() =>
								handleChange(TRAVEL_TIME_TYPE['all-day'])
							}
							onKeyDown={(e) =>
								handleKeyDown(e, TRAVEL_TIME_TYPE['all-day'])
							}
						>
							<RadioButtonInput
								className="cursor-pointer text-lg"
								control={control}
								formName={TRAVEL_PROGRAM_FORM}
								id="timeType"
								label="All Day (24 hours)"
								value={TRAVEL_TIME_TYPE['all-day']}
							/>
						</div>

						<div
							className={
								'flex items-center justify-between py-3 border-b border-slate-300 cursor-pointer'
							}
							onClick={() =>
								handleChange(TRAVEL_TIME_TYPE.weekdays)
							}
							onKeyDown={(e) =>
								handleKeyDown(e, TRAVEL_TIME_TYPE.weekdays)
							}
						>
							<RadioButtonInput
								className="cursor-pointer text-lg"
								control={control}
								formName={TRAVEL_PROGRAM_FORM}
								id="timeType"
								label="Weekdays"
								value={TRAVEL_TIME_TYPE.weekdays}
							/>
						</div>

						<div
							className={
								'flex items-center justify-between py-3 border-b border-slate-300 cursor-pointer'
							}
							onClick={() =>
								handleChange(TRAVEL_TIME_TYPE.custom)
							}
							onKeyDown={(e) =>
								handleKeyDown(e, TRAVEL_TIME_TYPE.custom)
							}
						>
							<RadioButtonInput
								className="cursor-pointer text-lg"
								control={control}
								formName={TRAVEL_PROGRAM_FORM}
								id="timeType"
								label="Custom Time"
								value={TRAVEL_TIME_TYPE.custom}
							/>
						</div>
						{timeTypeValue === TRAVEL_TIME_TYPE.custom && addTime}
					</div>
				</div>
			</SecondaryModal>
			<AddCustomTimeModal
				visible={showAddTimeModal}
				setVisible={setShowAddTimeModal}
				onHide={() => setShowAddTimeModal(false)}
			/>
		</>
	);
};

export default TimeModal;
